import { GridColDef } from '@mui/x-data-grid-premium';
import { EditableItemField, MemberCampaignItem } from '@retail/campaign/types';
import React from 'react';
import { useAppTFunction } from '@retail/app/i18n';
import { baseColumnProps, EditIconButtonWithTooltip } from './campaignItemsTableBaseColumns';
import { PricePerUnit } from '@retail/products/components';
import { Box, Stack, Tooltip } from '@mui/material';
import { ConditionTooltipInfo } from '@retail/calculus/components';
import { BooleanChip, StackedTextCell } from '@retail/components';
import { EditPriceCell } from '../EditPriceCell';
import { useEditSalesPrice } from './useEditSalesPrice';
import { RxReset } from 'react-icons/rx';
import { IconButtonWithTooltip } from '@shared/components';

interface Props {
  includeVat: boolean;
  getEditableFields: (item: MemberCampaignItem) => EditableItemField[];
  handleOverrideSalesPrice: (item: MemberCampaignItem, salesPrice: number) => Promise<void>;
  handleResetSalesPrice: (itemId: string) => void;
}

export const useGetMemberCampaignItemsColumns = ({
  includeVat,
  getEditableFields,
  handleOverrideSalesPrice,
  handleResetSalesPrice,
}: Props) => {
  const t = useAppTFunction();
  const {
    onSubmitSalesPrice,
    salesPriceFormMethods,
    salesPriceController,
    editingCellId,
    setEditingCellId,
  } = useEditSalesPrice({
    handleSubmitSalesPrice: handleOverrideSalesPrice,
  });

  const isImposedColumn: GridColDef<MemberCampaignItem> = {
    ...baseColumnProps,
    field: 'isImposed',
    headerName: t('campaign.columns.imposedCampaignPrice'),
    headerAlign: 'center',
    align: 'center',
    width: 180,
    renderCell: ({ row }) => (
      <BooleanChip
        value={row.isSalesPriceOverridable}
        label={
          row.isSalesPriceOverridable
            ? t('campaign.columns.notMandatory')
            : t('campaign.columns.mandatory')
        }
      />
    ),
    valueGetter: (_, row) => row.isSalesPriceOverridable,
  };

  const campaignSalesPriceColumn: GridColDef<MemberCampaignItem> = {
    ...baseColumnProps,
    width: 200,
    field: 'campaignSalesPrice',
    headerName: t('campaign.columns.campaignSalesPrice.label'),
    renderCell: ({ id, row }) => {
      const salesPrice = row.getSalesPriceForCampaignUnit();

      if (id === editingCellId) {
        const onCancel = () => {
          setEditingCellId(undefined);
          salesPriceFormMethods.reset();
        };
        return (
          <StackedTextCell
            topLine={
              <EditPriceCell
                onSubmit={onSubmitSalesPrice(row)}
                onCancel={onCancel}
                controller={salesPriceController}
                initValue={includeVat ? salesPrice?.priceWithVat : salesPrice?.priceWithoutVat}
              />
            }
            bottomLine={t('campaign.columns.incVat')}
          />
        );
      }

      const hasOverriddenPrice = !!row.memberCampaignSalesUnitPrices?.length;
      const isEditable = getEditableFields(row).includes('salesPrice');

      return (
        <StackedTextCell
          topLine={
            <>
              <Box display="flex" alignItems="center" gap={1}>
                <PricePerUnit
                  price={salesPrice?.getLocaleFormattedPrice(includeVat) ?? '-'}
                  unit={row.campaignUnit}
                />
                {isEditable && (
                  <EditIconButtonWithTooltip
                    title={`${t('campaign.columns.campaignSalesPrice.edit')} ${row.primaryText}`}
                    onClick={() => setEditingCellId(id)}
                    sx={{ p: 0 }}
                  />
                )}
              </Box>
              {hasOverriddenPrice && (
                <Box display="flex" alignItems="center" gap={1}>
                  <PricePerUnit
                    price={
                      row.getOriginalPriceForCampaignUnit()?.getLocaleFormattedPrice(includeVat) ??
                      '-'
                    }
                    unit={row.campaignUnit}
                    typographyVariant="caption"
                    lineThrough
                  />
                  {isEditable && (
                    <IconButtonWithTooltip
                      title={`${t('campaign.columns.campaignSalesPrice.reset')} ${row.primaryText}`}
                      onClick={() => handleResetSalesPrice(row.id)}
                      sx={{ p: 0 }}
                    >
                      <RxReset fontSize="small" />
                    </IconButtonWithTooltip>
                  )}
                </Box>
              )}
            </>
          }
          bottomLine={
            salesPrice
              ? includeVat
                ? t('campaign.columns.incVat')
                : t('campaign.columns.exVat')
              : undefined
          }
          slotProps={{ bottomLine: { fontSize: '13px' } }}
          gap="small"
        />
      );
    },
  };

  const localPurchasePriceColumn: GridColDef<MemberCampaignItem> = {
    ...baseColumnProps,
    width: 160,
    field: 'localPurchasePrice',
    headerName: t('campaign.columns.localPurchasePrice'),
    renderCell: ({ row }) => {
      const localPurchasePrice = row.getLocalPurchasePriceForCampaignUnit();

      return (
        <PricePerUnit
          price={localPurchasePrice?.getLocaleFormattedPrice(includeVat) ?? '-'}
          unit={row.campaignUnit}
        />
      );
    },
  };

  const localFreightColumn: GridColDef<MemberCampaignItem> = {
    ...baseColumnProps,
    width: 100,
    field: 'localFreight',
    headerName: t('campaign.columns.localFreightCondition'),
    renderCell: ({ row }) => {
      const freightCondition = row.localFreightCondition;

      if (!freightCondition) return '-';

      const freightConditionValue = freightCondition.fixedAddOn
        ? t('freight.columns.pricePerUnit', {
            price: freightCondition.fixedAddOn,
            unit: freightCondition.fixedAddOnUnit?.unit,
          })
        : freightCondition.percentAddon + '%';

      return (
        <Tooltip
          title={
            <ConditionTooltipInfo
              title=""
              bindingType={freightCondition?.condition.bindingType}
              bindingTarget={freightCondition?.condition.getLevelName() ?? ''}
            />
          }
        >
          <Stack
            border={(theme) => `1px solid ${theme.vars.palette.divider}`}
            p={1}
            borderRadius={2}
          >
            {freightConditionValue}
          </Stack>
        </Tooltip>
      );
    },
  };

  const localCostPriceColumn: GridColDef<MemberCampaignItem> = {
    ...baseColumnProps,
    width: 160,
    field: 'localCostPrice',
    headerName: t('campaign.columns.localCostPrice'),
    renderCell: ({ row }) => {
      const localCostPrice = row.getLocalCostPriceForCampaignUnit();

      return (
        <PricePerUnit
          price={localCostPrice?.getLocaleFormattedPrice(includeVat) ?? '-'}
          unit={row.campaignUnit}
        />
      );
    },
  };

  const localSalesPriceColumn: GridColDef<MemberCampaignItem> = {
    ...baseColumnProps,
    width: 160,
    field: 'localSalesPrice',
    headerName: t('campaign.columns.localSalesPrice'),
    renderCell: ({ row }) => {
      const localSalesPriceForCampaignUnit = row.getLocaSalesPriceForCampaignUnit();

      return (
        <PricePerUnit
          price={localSalesPriceForCampaignUnit?.getLocaleFormattedPrice(includeVat) ?? '-'}
          unit={row.campaignUnit}
        />
      );
    },
  };

  const localSalesCoverageColumn: GridColDef<MemberCampaignItem> = {
    ...baseColumnProps,
    field: 'localSalesCoverage',
    headerName: t('campaign.columns.localSalesCoverage'),
    renderCell: ({ row }) => row.getFormattedLocalSalesCoverage(),
  };

  const primaryDicountPercentageColumn: GridColDef<MemberCampaignItem> = {
    ...baseColumnProps,
    width: 128,
    field: 'primaryDicountPercentage',
    headerName: t('campaign.columns.discountPercentage'),
    renderCell: ({ row }) =>
      row.primaryDiscountPercentage ? `${row.primaryDiscountPercentage}%` : '-',
  };

  return {
    isImposedColumn,
    campaignSalesPriceColumn,
    localPurchasePriceColumn,
    localFreightColumn,
    localCostPriceColumn,
    localSalesPriceColumn,
    localSalesCoverageColumn,
    primaryDicountPercentageColumn,
  };
};
